import React, { useState, useEffect } from 'react';
import { Card, CardBody, Col, Row, Badge, Button, Spinner } from 'reactstrap';
import { Icon, UserAvatar } from '../../../components/Component';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import { formatDistanceToNow } from 'date-fns';
import { getPublicDocumentUrl } from '../../../utils/envConfig';
import { findUpper } from '../../../utils/Utils';
import { Link } from 'react-router-dom';

const LeadSummary = ({ user, onReassign, onBackToSearch, phone }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const axios = useAxiosPrivate();

  useEffect(() => {
    fetchLeadData();
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLeadData = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      const activitiesRes = await axios.get(`/activities/user/${user.id}?page=1&limit=10`);
      setActivities(activitiesRes.data?.activities || []);
    } catch (err) {
      console.error('Error fetching lead data:', err);
    } finally {
      setLoading(false);
    }
  };


  const getActivityIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'call': return 'call';
      case 'email': return 'mail';
      case 'whatsapp': return 'whatsapp';
      case 'viewing': return 'eye';
      default: return 'activity';
    }
  };

  const getActivityColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'call': return 'success';
      case 'email': return 'info';
      case 'whatsapp': return 'success';
      case 'viewing': return 'warning';
      default: return 'secondary';
    }
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner />
        <p className="mt-2">Loading lead summary...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="text-center mb-4">
        <h4>Lead Found</h4>
        <p className="text-muted">This phone number is already in the system</p>
      </div>

      <Card className="mb-4">
        <CardBody>
          <Row>
            <Col md="6">
              <h5>{user.first_name} {user.last_name}</h5>
              <p className="text-muted mb-1">
                <Icon name="call" className="me-1" />
                {phone}
                {user?.DndStatus && <Badge color="danger" className="ms-2">DND Number</Badge>}
              </p>
              {user?.tags && user.tags.length > 0 && (
                <div className="mt-1 mb-1">
                  <Icon name="tag" className="me-1" />
                  {user.tags.map((tag, index) => (
                    <Badge key={index} color="secondary" className="ms-1">
                      {tag.name || tag}
                    </Badge>
                  ))}
                </div>
              )}
              {user.email && (
                <p className="text-muted mb-1">
                  <Icon name="mail" className="me-1" />
                  {user.email}
                </p>
              )}
              <div className="mt-2 d-flex align-items-center">
                <Badge color={user.currentAgent ? 'success' : 'warning'}>
                  {user.currentAgent ? 'Assigned' : 'Unassigned'}
                </Badge>
                {user.isProspect && <Icon name="star" className="ms-1 text-warning" />}
              </div>
            </Col>
            <Col md="6">
              <div className="d-flex flex-column">
                {user.currentAgent && (
                  <div className="d-flex align-items-center mb-3">
                    <div className="me-3">
                      <h6 className="mb-0">Current Agent:</h6>
                    </div>
                    <div className="d-flex align-items-center">
                      <Link
                        to={`/user-profile/${user?.currentAgent?.userId?.id}/user-profile`}
                        className="d-flex align-items-center text-decoration-none"
                      >
                        <UserAvatar
                          text={findUpper(
                            user?.currentAgent?.userId?.first_name ??
                            user?.currentAgent?.user?.first_name ??
                            ''
                          )}
                          className={'sm'}
                          image={getPublicDocumentUrl(
                            user?.currentAgent?.userId?.photo ?? user?.currentAgent?.user?.photo
                          )}
                        />
                        <span className="ms-2 text-dark">
                          {user?.currentAgent?.userId?.first_name}{' '}
                          {user?.currentAgent?.userId?.last_name}
                        </span>
                      </Link>
                    </div>
                  </div>
                )}
                <div className="d-flex align-items-center">
                  <div className="me-3">
                    <h6 className="mb-0">Lead Source:</h6>
                  </div>
                  <div>
                    <span className="text-muted">{user.leadSource || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>

      <Card className="mb-4">
        <CardBody>
          <h6 className="mb-3">Recent Activities (Last 10)</h6>
          {activities.length > 0 ? (
            <div className="activity-timeline">
              {activities.map((activity, idx) => (
                <div key={idx} className="d-flex mb-3 pb-3" style={{ borderBottom: idx < activities.length - 1 ? '1px solid #e5e9f2' : 'none' }}>
                  <div className="me-3">
                    <div
                      className={`d-flex align-items-center justify-content-center rounded-circle`}
                      style={{
                        width: '32px',
                        height: '32px',
                        backgroundColor: getActivityColor(activity.type) === 'success' ? '#28a745' :
                          getActivityColor(activity.type) === 'info' ? '#17a2b8' :
                            getActivityColor(activity.type) === 'warning' ? '#ffc107' : '#6c757d',
                        color: 'white',
                        fontSize: '14px'
                      }}
                    >
                      <Icon name={getActivityIcon(activity.type)} />
                    </div>
                  </div>
                  <div className="flex-grow-1">
                    <div className="d-flex justify-content-between align-items-start">
                      <h6 className="mb-1 text-capitalize">{activity.type || 'Activity'}</h6>
                      <small className="text-muted">
                        {formatDistanceToNow(new Date(activity.completed_date ?? activity.date), { addSuffix: true })}
                      </small>
                    </div>
                    <p className="text-muted mb-1 small">{activity.notes || activity.description || 'No description'}</p>
                    {activity.agent && (
                      <small className="text-soft">
                        by {activity.agent.first_name} {activity.agent.last_name}
                      </small>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted py-3">No activities found</p>
          )}
        </CardBody>
      </Card>

      <div className="text-center">
        <Row>
          <Col>
            <Button color="primary" onClick={onReassign} className="me-2">
              {user.currentAgent ? 'Reassign Lead' : 'Assign Lead'}
            </Button>
            <Button color="light" onClick={onBackToSearch}>
              Search Different Number
            </Button>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default LeadSummary;
